import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiQueryOptions from 'lib/api/apiQueryOptions';
import downloadCsv from 'helpers/csv';
import useAgencyFanOutQueries from './useAgencyFanOutQueries';
import {
  agencyAppointmentsKeys,
  parsePendingAppointments,
  pendingAppointmentsRequest
} from './api/agencyAppointmentsZoho';

// zoho-style label for the client-side agency tag
const CSV_HEADER_LABELS = { agencyName: 'Agency_Name' };

// module level so the fan-out entries stay referentially stable across renders
function pendingEntry({ agencyId }) {
  return {
    queryKey: agencyAppointmentsKeys.pending(agencyId),
    request: pendingAppointmentsRequest(agencyId),
    parse: parsePendingAppointments
  };
}

function tagRows(rows, { agencyId, agencyName }) {
  return (rows ?? []).map(appt => ({ ...appt, agencyId, agencyName }));
}

/**
 * loads the open appointment requests of every applied agency.
 * @param agencies - list of { agencyId, agencyName } currently applied
 */
export default function usePendingAppointments(agencies) {
  const queryClient = useQueryClient();
  const fan = useAgencyFanOutQueries(agencies, pendingEntry);

  const pendingAppointments = useMemo(
    () =>
      fan.results.flatMap(({ agencyId, agencyName, data }) =>
        tagRows(data, { agencyId, agencyName })
      ),
    [fan.results]
  );

  // the export modal offers to include agencies the user picked but has not applied
  // yet, so it needs their rows before the render that applies them. fetchQuery
  // writes to the same cache slots, which makes that render a cache hit.
  const fetchPendingAppointments = useCallback(
    async (agenciesToFetch = []) => {
      const settled = await Promise.allSettled(
        agenciesToFetch.map(agency =>
          queryClient.fetchQuery(apiQueryOptions(pendingEntry(agency)))
        )
      );
      return settled.flatMap((result, i) =>
        result.status === 'fulfilled'
          ? tagRows(result.value, agenciesToFetch[i])
          : []
      );
    },
    [queryClient]
  );

  const exportPendingAppointments = useCallback(
    (desiredHeaders = [], rowsOverride) => {
      const rows = rowsOverride ?? pendingAppointments;
      if (!rows.length) return;

      const uniqueDesiredHeaders = new Set(desiredHeaders);
      const columns = Object.keys(rows[0])
        .filter(header => uniqueDesiredHeaders.has(header))
        .map(header => ({
          key: header,
          label: CSV_HEADER_LABELS[header] ?? header
        }));

      downloadCsv({
        filename: 'pending_appointments.csv',
        columns,
        rows
      });
    },
    [pendingAppointments]
  );

  return {
    pendingAppointments,
    loadingPendingAppointments: fan.isLoading,
    failedAgencies: fan.failedAgencies,
    retryFailed: fan.retryFailed,
    refetchPendingAppointments: fan.refetchAll,
    fetchPendingAppointments,
    exportPendingAppointments
  };
}
